import type { CsmSnapshotCompanyRow } from '../../../api/dashboard';
import { bareDomain } from '../../Sales/shared';

/** Throwaway workspaces people create to try the product: not customers, never a row. */
const TEST_DOMAIN = /^(awesomecompany|example|test|testing|demo|mailinator|localhost)(\.|$)/i;

/** True for a trial or placeholder domain ("awesomecompany.com", "example.org", "test.io"). */
export function isTestDomain(domain: string | null | undefined): boolean {
  const d = bareDomain(domain ?? '');
  return d !== '' && TEST_DOMAIN.test(d);
}

export interface SnapshotCompanyGroup extends CsmSnapshotCompanyRow {
  /** Platform workspaces folded into this row (2 for a company with two workspaces). */
  workspaces: number;
}

/**
 * Pure: one row per company for a night. Workspaces that resolve to the same company (`keyOf`: the CSM list entry,
 * else the domain) are summed, so a company with two workspaces ("ISS Facility Services UK" twice) reads once.
 * Test domains are dropped. Order of first appearance. Exported for tests.
 */
export function mergeSnapshotRows(
  rows: readonly CsmSnapshotCompanyRow[],
  keyOf: (row: CsmSnapshotCompanyRow) => string,
): SnapshotCompanyGroup[] {
  const byKey = new Map<string, SnapshotCompanyGroup>();
  for (const r of rows) {
    if (isTestDomain(r.domain)) continue;
    const key = keyOf(r);
    const g = byKey.get(key);
    if (!g) {
      byKey.set(key, { ...r, workspaces: 1 });
      continue;
    }
    g.workspaces += 1;
    g.memberCount += r.memberCount;
    g.activeUsers30d += r.activeUsers30d;
    g.loginCount30d += r.loginCount30d;
    g.totalTokens30d += r.totalTokens30d;
    g.yesterdayTokens += r.yesterdayTokens;
    g.activeRate30d = g.memberCount > 0 ? (g.activeUsers30d / g.memberCount) * 100 : 0;
    if (r.status === 'active') g.status = 'active';
    if (r.lastLoginAt && (!g.lastLoginAt || r.lastLoginAt > g.lastLoginAt)) g.lastLoginAt = r.lastLoginAt;
    if (r.lastTokenAt && (!g.lastTokenAt || r.lastTokenAt > g.lastTokenAt)) g.lastTokenAt = r.lastTokenAt;
  }
  return [...byKey.values()];
}
